import React from 'react';
import {
  Typography,
  Box,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import LoadingIndicator from '../LoadingIndicator';
import RefreshIcon from '@mui/icons-material/Refresh';
import GoalSectionCard from './GoalSectionCard';
import MessageFormatter from '../MessageFormatter';
import logo from '../../assets/images/Supervoima-Logo-White.svg';

/**
 * GoalAlignedReportLayout Component
 *
 * Displays complete goal-aligned assessment report with multiple goal sections.
 * Shows report metadata, cache status, and regeneration option.
 */
export default function GoalAlignedReportLayout({
  data,
  loading,
  error,
  onRegenerate,
  generating
}) {
  // Format timestamp to DD.MM.YYYY HH:MM format (Finnish standard with time)
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  // Format date string (YYYY-MM-DD) to DD.MM.YYYY
  const formatDateOnly = (dateStr) => {
    if (!dateStr) return '';
    // Handle ISO timestamp or date-only string
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Loading state (initial load only, not regeneration)
  if (loading && !generating) {
    return <LoadingIndicator />;
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  // No data state - show consistent blue header with generate button
  if (!data) {
    return (
      <Box sx={{ py: 4 }}>
        <Paper elevation={4} sx={{ p: 0, mb: 6, borderRadius: 3, overflow: 'hidden' }}>
          {/* Branded header with Generate button */}
          <Box sx={{
            backgroundColor: 'rgb(1, 28, 45)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <img src={logo} alt="Supervoima Logo" style={{ height: '36px', width: 'auto' }} />
              <Typography
                variant="h5"
                sx={{ fontFamily: 'Bebas Neue', letterSpacing: 1, mb: 0 }}
              >
                AI EVALUATION REPORT
              </Typography>
            </Box>
            {onRegenerate && (
              <Button
                onClick={onRegenerate}
                disabled={generating}
                variant="outlined"
                size="small"
                startIcon={generating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.6)',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  },
                  '&:disabled': {
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'rgba(255,255,255,0.5)'
                  }
                }}
              >
                {generating ? 'Generating...' : 'Generate Report'}
              </Button>
            )}
          </Box>
          {/* Content area with placeholder text or generating status */}
          <Box sx={{ p: 4, textAlign: 'center' }}>
            {generating ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={40} />
                <Typography variant="body1" color="text.secondary">
                  Analysing assessment data...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This may take 1-2 minutes depending on amount of data available
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Generate an AI Evaluation report. This can take 1-2 minutes depending on data amount.
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    );
  }

  const {
    org_name,
    goal_sections = [],
    report_metadata = {},
    generated_at,
    cached = false
  } = data;

  const { response_count, unique_user_count, time_range } = report_metadata;
  const isCombinedEvaluation = goal_sections.length === 1 && goal_sections[0]?.goal_template_id === 'combined';

  return (
    <Box sx={{ py: 4 }}>
      {/* Report Header */}
      <Paper elevation={4} sx={{ p: 0, mb: 6, borderRadius: 3, overflow: 'hidden' }}>
        {/* Branded header */}
        <Box sx={{
          backgroundColor: 'rgb(1, 28, 45)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <img src={logo} alt="Supervoima Logo" style={{ height: '36px', width: 'auto' }} />
            <Typography
              variant="h5"
              sx={{ fontFamily: 'Bebas Neue', letterSpacing: 1, mb: 0 }}
            >
              AI EVALUATION REPORT
            </Typography>
          </Box>
          {onRegenerate && (
            <Button
              onClick={onRegenerate}
              disabled={generating}
              variant="outlined"
              size="small"
              startIcon={generating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <RefreshIcon />}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.6)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              {generating ? 'Generating...' : 'Regenerate'}
            </Button>
          )}
        </Box>

        {/* Report body */}
        <Box sx={{ p: 4 }}>
          <Typography
            variant="h5"
            sx={{
              fontFamily: 'Bebas Neue',
              fontWeight: 'normal',
              letterSpacing: 1,
              color: 'text.primary'
            }}
            gutterBottom
          >
            AI Evaluation - {org_name}
          </Typography>

          {/* Report Metadata */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {generated_at && (
              <Chip
                label={`Generated: ${formatTimestamp(generated_at)}`}
                size="small"
                variant="outlined"
            />
          )}
          {cached && (
            <Chip
              label="Cached"
              size="small"
              color="info"
              variant="outlined"
            />
          )}
          {response_count !== undefined && (
            <Chip
              label={`${response_count} Unique responses`}
              size="small"
              variant="outlined"
            />
          )}
          {unique_user_count !== undefined && (
            <Chip
              label={`${unique_user_count} Unique users`}
              size="small"
              variant="outlined"
            />
          )}
            {time_range?.start_date && time_range?.end_date && (
              <Chip
                label={`Period: ${formatDateOnly(time_range.start_date)} - ${formatDateOnly(time_range.end_date)}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {isCombinedEvaluation && (
            <Box sx={{ mt: 3 }}>
              {goal_sections[0]?.executive_summary ? (
                <MessageFormatter content={goal_sections[0].executive_summary} />
              ) : (
                <Alert severity="warning">
                  Combined AI Evaluation content was not generated. Try regenerating the report.
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Goal Sections */}
      {isCombinedEvaluation ? null : goal_sections.length > 0 ? (
        <Box>
          {goal_sections.map((section, index) => (
            <GoalSectionCard
              key={section.goal_template_id || index}
              goalSection={section}
              defaultExpanded={index === 0}  // First section expanded by default
            />
          ))}
        </Box>
      ) : (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No goal sections were generated. This may indicate insufficient data or processing errors.
          Try selecting different goals or a different time period.
        </Alert>
      )}

      {/* Report Footer */}
      <Paper elevation={1} sx={{ p: 3, mt: 4, backgroundColor: 'rgba(1, 28, 45, 0.02)' }}>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          <strong>About this report:</strong> AI evaluation reports provide focused insights based on your organization's
          assessment. Each section synthesizes responses from interviews, highlighting key findings,
          evidence, and actionable recommendations specific to that goal. Analysis was conducted using AI-driven
          synthesis methods; double check before applying any recommendations.
        </Typography>
        {cached && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            This report was retrieved from cache. Click "Regenerate Report" to create a fresh analysis
            with the latest data.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
